import {
  fetchFlowRunSummary,
  fetchResourceApiKeys,
  loadDataSource,
  refreshPipeline,
  updateSourceInfo,
} from 'js/actions';
import {
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
} from 'js/constants/connectionCategories';
import { FILE_LIST_SORT_CRITERIA } from 'js/constants/paginationConstants';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useEffect } from 'react';

const fetchedResourceApiKeys = new Set<string>();
const fetchedFlowRunSummaries = new Set<string>();

export const useResourceApiKeys = (
  resourceType: string,
  data: any,
  apiKeys: any,
  areActionsExpanded: boolean,
) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (resourceType === RESOURCE_TYPE_SOURCE && data?.id && areActionsExpanded) {
      const resourceKey = `${resourceType}_${data.id}`;

      if (!fetchedResourceApiKeys.has(resourceKey)) {
        const hasApiKeys =
          apiKeys &&
          (Array.isArray(apiKeys) ? apiKeys.length > 0 : Object.keys(apiKeys).length > 0);

        if (!hasApiKeys) {
          fetchedResourceApiKeys.add(resourceKey);
          dispatch(fetchResourceApiKeys('data_sources', data.id));
        } else {
          fetchedResourceApiKeys.add(resourceKey);
        }
      }
    }
  }, [resourceType, data?.id, apiKeys, areActionsExpanded, dispatch]);
};

export const useFlowRunSummary = (
  resourceType: string,
  data: any,
  flowRunSummary: any,
  areActionsExpanded: boolean,
) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (resourceType === RESOURCE_TYPE_SOURCE && data?.nodeKey && areActionsExpanded) {
      const summaryKey = `${data.nodeKey}_summary`;

      if (!fetchedFlowRunSummaries.has(summaryKey)) {
        const hasSummaryData = Object.keys(flowRunSummary?.[data.nodeKey]?.data || {}).length > 0;

        if (!hasSummaryData) {
          fetchedFlowRunSummaries.add(summaryKey);

          const now = new Date();
          const startDate = data?.created_at ? new Date(data.created_at) : now;
          const endOfDay = new Date(now);
          endOfDay.setHours(23, 59, 59, 999);

          dispatch(
            fetchFlowRunSummary(data.nodeKey, {
              page: 1,
              per_page: 100,
              orderby: FILE_LIST_SORT_CRITERIA.LAST_CREATED_DESC.value,
              from: startDate.toISOString(),
              to: endOfDay.toISOString(),
            }),
          );
        } else {
          fetchedFlowRunSummaries.add(summaryKey);
        }
      }
    }
  }, [resourceType, data?.nodeKey, flowRunSummary, areActionsExpanded, dispatch]);
};

export const useUpdateConfig = (resourceId: string) => {
  const dispatch = useAppDispatch();

  const handleUpdateConfig = (
    newConfig: any,
    data: any,
    connectionCategory: string,
    onSuccess?: () => void,
  ) => {
    const isTemplatizedCategory = [
      CONNECTION_CATEGORY_TEMPLATIZED_API,
      CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
    ].includes(connectionCategory);

    // For templatized API sources, fetch if vendor_endpoint not already present
    const sourcePromise =
      isTemplatizedCategory && !data?.vendor_endpoint
        ? dispatch(loadDataSource(resourceId)).then(
            ({ result, entities }) => entities.sources[result],
          )
        : Promise.resolve(data);

    sourcePromise
      .then((source) => {
        const { vendor_endpoint: vendor_endpoint_id } = source;
        // If vendor_endpoint has valid id, it's template mode - use template_config
        // Otherwise it's Advanced Mode - use source_config
        const useTemplateConfig = isTemplatizedCategory && !!vendor_endpoint_id;
        // For template mode: merge newConfig into source.template_config
        // For advanced mode / regular sources: use newConfig directly (original behavior)
        const payload = useTemplateConfig
          ? {
              template_config: {
                ...source.template_config,
                ...newConfig,
              },
              vendor_endpoint_id,
            }
          : { source_config: newConfig };

        return dispatch(updateSourceInfo(resourceId, payload, connectionCategory));
      })
      .then(() => {
        if (data?.origin_node_id) {
          dispatch(refreshPipeline(data.origin_node_id));
        }
        if (onSuccess) onSuccess();
      });
  };

  return { handleUpdateConfig };
};
